import {
  Badge,
  Button,
  Card,
  Disclosure,
  buttonVariants,
  cn,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { displayError } from "../api/errors.js";
import { ErrorState, LoadingState } from "../components/states.js";
import {
  createPlanDraft,
  getContentPacks,
  listGeneratedTasks,
  listPlanInstances,
  needsReview,
  planQuestions,
  publishPlan,
  recurrenceLabel,
  reviewPlan,
  updatePlanCard,
  windowStartLabel,
  type PlanCardContent,
  type PlanInstance,
  type PlanPack,
} from "./plan-api.js";

const publishedDate = new Intl.DateTimeFormat("en-NZ", { dateStyle: "medium" });

function packForInstance(
  packs: readonly PlanPack[],
  instance: PlanInstance,
): PlanPack | undefined {
  return packs.find((pack) => pack.code === instance.plan_pack_code);
}

function AnswerToggle({
  applicable,
  disabled,
  label,
  onChange,
}: {
  applicable: boolean;
  disabled: boolean;
  label: string;
  onChange: (applicable: boolean) => void;
}) {
  const option = (value: boolean, text: string) => (
    <button
      aria-pressed={applicable === value}
      className={cn(
        "focus-inset h-11 min-w-16 flex-1 px-4 text-sm font-semibold transition disabled:opacity-60 sm:h-10 sm:flex-none",
        applicable === value
          ? "bg-emerald-800 text-white"
          : "bg-surface text-stone-700 hover:bg-stone-100",
      )}
      disabled={disabled}
      onClick={() => {
        if (applicable !== value) onChange(value);
      }}
      type="button"
    >
      {text}
    </button>
  );
  return (
    <div
      aria-label={label}
      className="flex shrink-0 divide-x divide-stone-300 overflow-hidden rounded-md border border-stone-300 sm:inline-flex"
      role="group"
    >
      {option(true, "Yes")}
      {option(false, "No")}
    </div>
  );
}

function QuestionRows({
  cards,
  instance,
  onAnswer,
  saving,
}: {
  cards: readonly PlanCardContent[];
  instance: PlanInstance;
  onAnswer: (card: PlanCardContent, applicable: boolean) => void;
  saving: boolean;
}) {
  return (
    <ul className="divide-y divide-stone-200">
      {cards.map((card) => {
        const applicable =
          instance.cards.find((entry) => entry.card_code === card.code)
            ?.applicable ?? card.default_applicable;
        return (
          <li
            className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:px-5"
            key={card.code}
          >
            <p className="text-sm font-medium text-stone-900">
              {card.applicability_question}
            </p>
            <AnswerToggle
              applicable={applicable}
              disabled={saving}
              label={card.applicability_question ?? card.title}
              onChange={(value) => onAnswer(card, value)}
            />
          </li>
        );
      })}
    </ul>
  );
}

function PlanQuestions({
  instance,
  pack,
}: {
  instance: PlanInstance;
  pack: PlanPack;
}) {
  const queryClient = useQueryClient();
  const queryKey = ["plan-instances", instance.location_id];
  const { common, specialist } = planQuestions(pack);
  const answer = useMutation({
    mutationFn: ({
      applicable,
      card,
    }: {
      applicable: boolean;
      card: PlanCardContent;
    }) =>
      updatePlanCard(instance.id, {
        applicable,
        card_code: card.code,
        reason: applicable ? null : "Not used at this location",
      }),
    onMutate: ({ applicable, card }) => {
      queryClient.setQueryData<PlanInstance[]>(queryKey, (current) =>
        current?.map((entry) =>
          entry.id === instance.id
            ? {
                ...entry,
                cards: entry.cards.map((decision) =>
                  decision.card_code === card.code
                    ? { ...decision, applicable }
                    : decision,
                ),
              }
            : entry,
        ),
      );
    },
    onError: () => queryClient.invalidateQueries({ queryKey }),
    onSuccess: (updated) => {
      queryClient.setQueryData<PlanInstance[]>(queryKey, (current) =>
        current?.map((entry) => (entry.id === updated.id ? updated : entry)),
      );
    },
  });
  const publish = useMutation({
    mutationFn: () => publishPlan(instance.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey }),
        queryClient.invalidateQueries({
          queryKey: ["plan-checks", instance.location_id],
        }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["locations"] }),
      ]);
    },
  });
  const specialistOn = specialist.filter(
    (card) =>
      instance.cards.find((entry) => entry.card_code === card.code)?.applicable,
  ).length;
  const onAnswer = (card: PlanCardContent, applicable: boolean) =>
    answer.mutate({ applicable, card });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-stone-950">
          Food control plan
        </h2>
        <Badge>{pack.version_label}</Badge>
      </div>
      <Card className="overflow-hidden">
        <QuestionRows
          cards={common}
          instance={instance}
          onAnswer={onAnswer}
          saving={publish.isPending}
        />
      </Card>
      {specialist.length > 0 ? (
        <Disclosure
          defaultOpen={specialistOn > 0}
          summary={
            specialistOn > 0
              ? `Specialist processes (${specialistOn})`
              : "Specialist processes"
          }
        >
          <Card className="overflow-hidden">
            <QuestionRows
              cards={specialist}
              instance={instance}
              onAnswer={onAnswer}
              saving={publish.isPending}
            />
          </Card>
        </Disclosure>
      ) : null}
      {answer.error || publish.error ? (
        <p
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
          role="alert"
        >
          {displayError(answer.error ?? publish.error)}
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button
          disabled={publish.isPending || answer.isPending}
          onClick={() => publish.mutate()}
        >
          {publish.isPending ? "Publishing…" : "Publish plan"}
        </Button>
      </div>
    </div>
  );
}

function PublishedPlan({
  instance,
  locationId,
  onChange,
  changing,
  pack,
}: {
  changing: boolean;
  instance: PlanInstance;
  locationId: string;
  onChange: () => void;
  pack: PlanPack;
}) {
  const queryClient = useQueryClient();
  const review = useMutation({
    mutationFn: () => reviewPlan(instance.id),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["plan-instances", locationId],
        }),
        queryClient.invalidateQueries({ queryKey: ["locations"] }),
      ]),
  });
  const tasks = useQuery({
    queryFn: () => listGeneratedTasks(locationId),
    queryKey: ["generated-plan-tasks", locationId, instance.id],
    refetchInterval: (query) => (query.state.data?.length ? false : 5_000),
  });
  const active = (tasks.data ?? []).filter(
    (task) => task.status !== "archived",
  );
  const unassigned = active.some(
    (task) =>
      (
        task.assignee_staff_profile_ids ??
        (task.assignee_staff_profile_id ? [task.assignee_staff_profile_id] : [])
      ).length === 0,
  );

  return (
    <div className="space-y-5">
      <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-stone-950">
            Food control plan
          </h2>
          <p className="mt-1 text-sm text-stone-500">
            {pack.version_label}
            {instance.effective_from
              ? ` · Published ${publishedDate.format(new Date(instance.effective_from))}`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {needsReview(instance) ? (
            <Button
              disabled={review.isPending}
              onClick={() => review.mutate()}
            >
              {review.isPending ? "Saving…" : "Looks right"}
            </Button>
          ) : null}
          <Button disabled={changing} onClick={onChange} variant="outline">
            {changing ? "Opening…" : "Change answers"}
          </Button>
        </div>
        {review.error ? (
          <p className="w-full text-sm font-semibold text-red-800" role="alert">
            {displayError(review.error)}
          </p>
        ) : null}
      </Card>

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-stone-950">Checks</h2>
          {active.length > 0 ? (
            <Link
              className={buttonVariants({
                size: "sm",
                variant: unassigned ? "default" : "outline",
              })}
              search={{ section: "assign" }}
              to="/setup"
            >
              Choose who does each check
            </Link>
          ) : null}
        </div>
        {tasks.isPending || (tasks.data && active.length === 0) ? (
          <LoadingState label="Preparing checks" />
        ) : null}
        {tasks.error ? (
          <ErrorState
            message={displayError(tasks.error)}
            onAction={() => void tasks.refetch()}
            title="Couldn’t load checks"
          />
        ) : null}
        {active.length > 0 ? (
          <Card className="overflow-hidden">
            <ul className="divide-y divide-stone-200">
              {active.map((task) => (
                <li
                  className="flex items-center gap-3 px-4 py-3 sm:px-5"
                  key={task.id}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{task.title}</p>
                    <p className="mt-0.5 text-xs text-stone-500">
                      {[
                        recurrenceLabel(task.schedule?.rrule),
                        windowStartLabel(task.schedule?.due_window_start),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  {task.status === "paused" ? <Badge>Paused</Badge> : null}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </section>
    </div>
  );
}

export function PlanWorkspace({
  canManage,
  locationId,
}: {
  canManage: boolean;
  locationId: string;
}) {
  const queryClient = useQueryClient();
  const packs = useQuery({
    queryFn: getContentPacks,
    queryKey: ["content-packs"],
    staleTime: 300_000,
  });
  const instances = useQuery({
    queryFn: () => listPlanInstances(locationId),
    queryKey: ["plan-instances", locationId],
    // Whether to start a draft is decided on a list fetched for this visit.
    refetchOnMount: "always",
  });
  const draft = instances.data?.find((instance) => instance.state === "draft");
  const published = instances.data?.find(
    (instance) => instance.state === "published",
  );
  const allPacks = packs.data?.packs ?? [];
  const activePacks = allPacks.filter((pack) => pack.status === "active");
  const selectedPack = draft
    ? packForInstance(allPacks, draft)
    : published
      ? packForInstance(allPacks, published)
      : activePacks[0];
  const create = useMutation({
    mutationFn: ({ clone, packCode }: { clone: boolean; packCode: string }) =>
      createPlanDraft({
        clone_current: clone,
        location_id: locationId,
        plan_pack_code: packCode,
      }),
    onSuccess: async (created) => {
      queryClient.setQueryData<PlanInstance[]>(
        ["plan-instances", locationId],
        (current) => [
          ...(current ?? []).filter((entry) => entry.id !== created.id),
          created,
        ],
      );
      await queryClient.invalidateQueries({
        queryKey: ["plan-instances", locationId],
      });
    },
  });

  // A location without a plan goes straight to the questions; the draft
  // starts from the plan's own default answers.
  const started = useRef<string | null>(null);
  // Only a list fetched for this visit, so a cached one can't make a second draft.
  const needsDraft =
    canManage &&
    instances.isSuccess &&
    instances.isFetchedAfterMount &&
    !instances.isFetching &&
    !draft &&
    !published &&
    selectedPack !== undefined;
  useEffect(() => {
    if (!needsDraft || !selectedPack || started.current === locationId) return;
    started.current = locationId;
    create.mutate({ clone: false, packCode: selectedPack.code });
  }, [create, locationId, needsDraft, selectedPack]);

  if (!canManage) return null;
  if (packs.isPending || instances.isPending)
    return <LoadingState label="Loading plan" />;
  if (packs.error || instances.error)
    return (
      <ErrorState
        message={displayError(packs.error ?? instances.error)}
        onAction={() => {
          void packs.refetch();
          void instances.refetch();
        }}
        title="Couldn’t load the plan"
      />
    );
  if (!selectedPack)
    return (
      <ErrorState message="Try again later." title="No plan available yet" />
    );
  if (create.error)
    return (
      <ErrorState
        message={displayError(create.error)}
        onAction={() => {
          started.current = null;
          create.reset();
        }}
        title="Couldn’t start the plan"
      />
    );

  if (draft) return <PlanQuestions instance={draft} pack={selectedPack} />;
  if (published)
    return (
      <PublishedPlan
        changing={create.isPending}
        instance={published}
        locationId={locationId}
        onChange={() =>
          create.mutate({
            clone: true,
            packCode: activePacks[0]?.code ?? selectedPack.code,
          })
        }
        pack={selectedPack}
      />
    );
  return <LoadingState label="Loading plan" />;
}
